import SendIcon from "@mui/icons-material/Send";
import { Alert, Avatar, Box, CircularProgress, Divider, IconButton, Link as MuiLink, Paper, Stack, TextField, Typography } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { createScopedLogger, formatString } from "../../../common";
import { formatTimestamp } from "../../../common/formatTimestamp";
import getNestedPropertyValue from "../../../common/getNestedPropertyValue";
import { useErrorHandler } from "../../../common/hooks";
import { VariableNames } from "../../Constants/VariableNames";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useObservedExpression from "../../Hooks/useObservedExpression";
import useObservedText from "../../Hooks/useObservedText";
import { ListMessageChatItemModel, ListMessageChatModel, ListMessageChatTimestampDisplayFormat } from "../../Models/ListMessageChatModel";
import IComponentProps from "../IComponentProps";
import ListPanelHeader from "../ListShared/ListPanelHeader";
import getComponentLayout from "../Table/getComponentLayout";

const logger = createScopedLogger("ListMessageChatComponent");
const LIST_MESSAGE_CHAT_MESSAGE_PATH_SEGMENT = "message";
const LIST_MESSAGE_CHAT_ITEM_TYPE = "ListMessageChatItemModel";

dayjs.extend(relativeTime);

function coerceMessages(value: any): Record<string, any>[] {
  if (value === undefined || value === null || value === false) {
    return [];
  }

  const items = Array.isArray(value) ? value : value.Items ?? value.items ?? value.Messages ?? value.messages;
  if (!Array.isArray(items)) {
    logger.error("Items expression did not return an array or an object with an Items/Messages array:", value);
    throw new Error("Items expression did not return an array or an object with an Items/Messages array: " + value);
  }

  return items;
}

function getFieldValue(item: Record<string, any>, field?: string) {
  if (!item || !field) {
    return undefined;
  }

  return getNestedPropertyValue(field, item);
}

function isAbsoluteUrl(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  return url.includes("://");
}

function getMessageTemplate(component: ListMessageChatModel): ListMessageChatItemModel {
  return component.Message?.[0] ?? component;
}

function formatMessageTimestamp(timestamp: unknown, displayFormat?: ListMessageChatTimestampDisplayFormat): string {
  if (displayFormat === ListMessageChatTimestampDisplayFormat.Relative && (timestamp instanceof Date || typeof timestamp === "string")) {
    return dayjs(timestamp).fromNow();
  }

  if (timestamp instanceof Date || typeof timestamp === "string") {
    return formatTimestamp(timestamp);
  }

  return String(timestamp ?? "");
}

function formatMessageTimestampTitle(timestamp: unknown, displayFormat?: ListMessageChatTimestampDisplayFormat): string | undefined {
  if (displayFormat === ListMessageChatTimestampDisplayFormat.Relative && (timestamp instanceof Date || typeof timestamp === "string")) {
    return formatTimestamp(timestamp);
  }

  return undefined;
}

function isNonEmptyResult(value: unknown): boolean {
  if (value === undefined || value === null || value === false) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }

  return true;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return String(error ?? "Message could not be posted.");
}

function toPostedMessage(value: unknown, text: string, template: ListMessageChatItemModel, currentContactId: unknown): Record<string, any> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, any>;
  }

  return {
    [template.ContentField ?? "Content"]: typeof value === "string" ? value : text,
    [template.TimestampField ?? "Timestamp"]: new Date().toISOString(),
    [template.ContactIdField ?? "ContactId"]: currentContactId,
  };
}

type MessageBubbleProps = {
  component: ListMessageChatModel;
  componentPath: string[];
  message: Record<string, any>;
  index: number;
  template: ListMessageChatItemModel;
  currentContactId: unknown;
  side: "left" | "right";
};

const MessageBubble: React.FC<MessageBubbleProps> = ({ component, componentPath, message, index, template, currentContactId, side }) => {
  const content = getFieldValue(message, template.ContentField ?? "Content");
  const timestamp = getFieldValue(message, template.TimestampField ?? "Timestamp");
  const timestampDisplayFormat = template.TimestampDisplayFormat ?? ListMessageChatTimestampDisplayFormat.Timestamp;
  const contactName = getFieldValue(message, template.ContactNameField ?? "ContactName");
  const contactId = getFieldValue(message, template.ContactIdField ?? "ContactId");
  const contactLink = template.ContactLink ? formatString(template.ContactLink, message) : undefined;
  const contactLinkProps = isAbsoluteUrl(contactLink) ? { href: contactLink } : { component: RouterLink, to: contactLink ?? "/" };
  const isCurrentContact = currentContactId !== undefined && currentContactId !== null && String(contactId) === String(currentContactId);
  const isRightSide = side === "right";
  const align = isRightSide ? "flex-end" : "flex-start";
  const selectionProps = useComponentSelection({
    component: {
      Id: `${component.Id}-message-${index}`,
      Type: LIST_MESSAGE_CHAT_ITEM_TYPE,
      Layout: {},
    },
    componentPath: [...componentPath, LIST_MESSAGE_CHAT_MESSAGE_PATH_SEGMENT, "0"],
    isContainer: false,
  });

  return (
    <Box
      {...selectionProps}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: align,
        width: "100%",
        pl: isRightSide ? { xs: 4, sm: 8 } : 0,
        pr: isRightSide ? 0 : { xs: 4, sm: 8 },
      }}
    >
      {!isCurrentContact && contactName && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25, maxWidth: "100%" }}>
          <Avatar sx={{ width: 22, height: 22, fontSize: 11 }}>{String(contactName).slice(0, 1).toUpperCase()}</Avatar>
          {contactLink ? (
            <MuiLink {...contactLinkProps} underline="hover" variant="caption" color="text.secondary">
              {String(contactName)}
            </MuiLink>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {String(contactName)}
            </Typography>
          )}
        </Box>
      )}
      <Box
        sx={{
          maxWidth: "100%",
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: isCurrentContact ? "primary.main" : "action.selected",
          color: isCurrentContact ? "primary.contrastText" : "text.primary",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        <Typography variant="body2">{String(content ?? "")}</Typography>
      </Box>
      {timestamp && (
        <Typography variant="caption" color="text.secondary" title={formatMessageTimestampTitle(timestamp, timestampDisplayFormat)} sx={{ mt: 0.25, mx: 1 }}>
          {formatMessageTimestamp(timestamp, timestampDisplayFormat)}
        </Typography>
      )}
    </Box>
  );
};

const ListMessageChatComponent: React.FC<IComponentProps<ListMessageChatModel>> = ({ component, context, componentPath = [] }) => {
  const errorHandler = useErrorHandler();
  const componentLayoutSx = getComponentLayout(component);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messageTemplate = getMessageTemplate(component);
  const headerTitle = useObservedText(component.HeaderTitle, context, "ListMessageChat.HeaderTitle");
  const [messages = [], messagesPending, refreshMessages] = useObservedExpression<Record<string, any>[]>(
    component.Items,
    context,
    "ListMessageChat.Items",
    coerceMessages
  );
  const [currentContactId] = useObservedExpression<unknown>(component.CurrentContactId, context, "ListMessageChat.CurrentContactId");
  const [postedMessages, setPostedMessages] = useState<Record<string, any>[]>([]);
  const [draft, setDraft] = useState("");
  const [isPosting, setIsPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);

  const allMessages = useMemo(() => [...messages, ...postedMessages], [messages, postedMessages]);
  const messageSideByContactId = useMemo(() => {
    const sides = new Map<string, "left" | "right">();
    let nextOtherSide: "left" | "right" = "left";
    const contactIdField = messageTemplate.ContactIdField ?? "ContactId";

    for (const message of allMessages) {
      const contactId = getFieldValue(message, contactIdField);
      if (contactId === undefined || contactId === null) {
        continue;
      }

      const contactKey = String(contactId);
      if (sides.has(contactKey)) {
        continue;
      }

      if (currentContactId !== undefined && currentContactId !== null && contactKey === String(currentContactId)) {
        sides.set(contactKey, "right");
        continue;
      }

      sides.set(contactKey, nextOtherSide);
      nextOtherSide = nextOtherSide === "left" ? "right" : "left";
    }

    return sides;
  }, [allMessages, currentContactId, messageTemplate.ContactIdField]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [allMessages.length]);

  const selectionProps = useComponentSelection({
    component,
    componentPath,
    isContainer: false,
    isEmpty: allMessages.length === 0,
  });

  const handlePostMessage = async () => {
    const text = draft.trim();
    if (!text || !component.OnMessagePosted) {
      return;
    }

    setIsPosting(true);
    setPostError(null);
    try {
      const eventContext = context.CreateChild();
      eventContext.VariableScope.set(VariableNames.Event, { text });
      const result = await eventContext.Evaluator.evaluate(component.OnMessagePosted, eventContext);

      if (isNonEmptyResult(result)) {
        setPostedMessages((current) => [...current, toPostedMessage(result, text, messageTemplate, currentContactId)]);
      }

      setDraft("");
    } catch (error) {
      setPostError(getErrorMessage(error));
      errorHandler(error);
    } finally {
      setIsPosting(false);
    }
  };

  const handleDraftKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && event.ctrlKey) {
      event.preventDefault();
      handlePostMessage();
    }
  };

  const canPost = !!component.OnMessagePosted;
  const surfaceSx = {
    ...componentLayoutSx,
    display: "flex",
    flexDirection: "column",
    minHeight: componentLayoutSx.minHeight ?? componentLayoutSx.height ?? 360,
    overflow: "hidden",
  };
  const chatContent = (
    <>
      <ListPanelHeader
        title={headerTitle}
        iconName={component.HeaderIcon}
        showRefresh={true}
        refreshTitle="Refresh messages"
        refreshAriaLabel="refresh messages"
        isRefreshing={messagesPending}
        onRefresh={() => refreshMessages()}
        actions={component.HeaderActions}
        context={context}
        ownerComponentId={component.Id}
        ownerComponentPath={componentPath}
      />
      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        <Stack sx={{ height: "100%", overflow: "auto", px: 2, py: 1.5, gap: 1.25 }}>
          {allMessages.map((message, index) => (
            <MessageBubble
              key={message.Id ?? message.id ?? index}
              component={component}
              componentPath={componentPath}
              message={message}
              index={index}
              template={messageTemplate}
              currentContactId={currentContactId}
              side={messageSideByContactId.get(String(getFieldValue(message, messageTemplate.ContactIdField ?? "ContactId"))) ?? "left"}
            />
          ))}
          <div ref={messagesEndRef} />
        </Stack>
        {messagesPending && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
      </Box>
      {canPost && (
        <>
          <Divider />
          <Box sx={{ p: 1 }}>
            {postError && (
              <Alert severity="error" onClose={() => setPostError(null)} sx={{ mb: 1 }}>
                {postError}
              </Alert>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={5}
                size="small"
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value);
                  setPostError(null);
                }}
                onKeyDown={handleDraftKeyDown}
                placeholder="Type a message..."
                disabled={isPosting}
              />
              <IconButton color="primary" onClick={handlePostMessage} disabled={!draft.trim() || isPosting} loading={isPosting} aria-label="send message">
                <SendIcon />
              </IconButton>
            </Stack>
          </Box>
        </>
      )}
    </>
  );
  const chatSurface =
    component.UsePaper === false ? (
      <Box sx={surfaceSx} {...selectionProps}>
        {chatContent}
      </Box>
    ) : (
      <Paper elevation={1} sx={surfaceSx} {...selectionProps}>
        {chatContent}
      </Paper>
    );

  return chatSurface;
};

export default ListMessageChatComponent;
